/**
 * Comment Anchor Service
 * 
 * Manages text anchoring for comments across different editor types.
 * Provides position tracking that survives document edits.
 */

/**
 * Anchor position representation that can survive edits
 * Uses a combination of:
 * - Relative offset from start of document/section
 * - Context strings before/after for validation
 * - Paragraph/block identifier for structure changes
 */
class CommentAnchor {
    constructor(data) {
        this.id = data.id || `anchor-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        this.documentType = data.documentType; // 'spec' or 'claims'
        this.startOffset = data.startOffset; // Absolute position at creation time
        this.endOffset = data.endOffset;
        this.text = data.text; // The actual selected text
        this.contextBefore = data.contextBefore; // Text before selection (for reconciliation)
        this.contextAfter = data.contextAfter; // Text after selection
        this.blockPath = data.blockPath; // Path to block in document structure
        this.lastValidatedOffset = data.startOffset; // Last known good position
        this.isValid = true;
    }

    /**
     * Create a JSON representation for storage
     */
    toJSON() {
        return {
            id: this.id,
            documentType: this.documentType,
            startOffset: this.startOffset,
            endOffset: this.endOffset,
            text: this.text,
            contextBefore: this.contextBefore,
            contextAfter: this.contextAfter,
            blockPath: this.blockPath,
            lastValidatedOffset: this.lastValidatedOffset,
            isValid: this.isValid
        };
    }
}

class CommentAnchorService {
    constructor() {
        this.anchors = new Map(); // anchorId -> CommentAnchor
        this.editorAnchors = new Map(); // editorInstance -> Set<anchorId>
        this.decorations = new Map(); // editorInstance -> decorations
    }

    /**
     * Create an anchor from current selection in spec editor (TipTap/ProseMirror)
     */
    createSpecAnchor(editor, commentId) {
        const { state } = editor;
        const { selection } = state;
        
        if (selection.empty) {
            console.warn('Cannot create anchor from empty selection');
            return null;
        }

        const { from, to } = selection;
        const selectedText = state.doc.textBetween(from, to, '\n');
        
        // Get context for reconciliation (20 chars before/after)
        const contextBefore = from > 0 ? 
            state.doc.textBetween(Math.max(0, from - 20), from, '\n') : '';
        const contextAfter = to < state.doc.content.size ? 
            state.doc.textBetween(to, Math.min(state.doc.content.size, to + 20), '\n') : '';

        // Get block path for structural tracking
        const $from = state.doc.resolve(from);
        const blockPath = this._getBlockPath($from);

        const anchor = new CommentAnchor({
            id: `${commentId}-anchor`,
            documentType: 'spec',
            startOffset: from,
            endOffset: to,
            text: selectedText,
            contextBefore,
            contextAfter,
            blockPath
        });

        this.anchors.set(anchor.id, anchor);
        this._addEditorAnchor(editor, anchor.id);
        this._updateSpecDecorations(editor);

        return anchor;
    }

    /**
     * Create an anchor from current selection in claims editor (CodeMirror)
     */
    createClaimsAnchor(view, commentId) {
        const { state } = view;
        const selection = state.selection.main;
        
        if (selection.empty) {
            console.warn('Cannot create anchor from empty selection');
            return null;
        }

        const { from, to } = selection;
        const selectedText = state.doc.sliceString(from, to);
        
        // Get line-based position for claims (more stable than char offset)
        const fromLine = state.doc.lineAt(from);
        const toLine = state.doc.lineAt(to);
        
        // Get context
        const contextBefore = state.doc.sliceString(Math.max(0, from - 20), from);
        const contextAfter = state.doc.sliceString(to, Math.min(state.doc.length, to + 20));

        const anchor = new CommentAnchor({
            id: `${commentId}-anchor`,
            documentType: 'claims',
            startOffset: from,
            endOffset: to,
            text: selectedText,
            contextBefore,
            contextAfter,
            blockPath: {
                startLine: fromLine.number,
                endLine: toLine.number,
                startCol: from - fromLine.from,
                endCol: to - toLine.from
            }
        });

        this.anchors.set(anchor.id, anchor);
        this._addEditorAnchor(view, anchor.id);
        this._updateClaimsDecorations(view);

        return anchor;
    }

    /**
     * Reconcile anchor positions after document changes
     */
    reconcileSpecAnchors(editor) {
        const editorAnchors = this.editorAnchors.get(editor);
        if (!editorAnchors) return;

        const { state } = editor;
        let needsUpdate = false;

        for (const anchorId of editorAnchors) {
            const anchor = this.anchors.get(anchorId);
            if (!anchor || anchor.documentType !== 'spec') continue;

            // Try to find the anchored text in the document
            const newPosition = this._findTextInDocument(
                state.doc,
                anchor.text,
                anchor.contextBefore,
                anchor.contextAfter,
                anchor.lastValidatedOffset
            );

            if (newPosition) {
                anchor.startOffset = newPosition.from;
                anchor.endOffset = newPosition.to;
                anchor.lastValidatedOffset = newPosition.from;
                anchor.isValid = true;
                needsUpdate = true;
            } else {
                // Mark as invalid if we can't find the text
                anchor.isValid = false;
                needsUpdate = true;
            }
        }

        if (needsUpdate) {
            this._updateSpecDecorations(editor);
        }
    }

    /**
     * Reconcile anchor positions for claims editor
     */
    reconcileClaimsAnchors(view) {
        const editorAnchors = this.editorAnchors.get(view);
        if (!editorAnchors) return;

        const { state } = view;
        let needsUpdate = false;

        for (const anchorId of editorAnchors) {
            const anchor = this.anchors.get(anchorId);
            if (!anchor || anchor.documentType !== 'claims') continue;

            // For claims, try line-based reconciliation first
            if (anchor.blockPath) {
                const startLine = state.doc.line(anchor.blockPath.startLine);
                if (startLine) {
                    const newFrom = startLine.from + anchor.blockPath.startCol;
                    const newTo = newFrom + anchor.text.length;
                    
                    // Verify the text matches
                    if (state.doc.sliceString(newFrom, newTo) === anchor.text) {
                        anchor.startOffset = newFrom;
                        anchor.endOffset = newTo;
                        anchor.lastValidatedOffset = newFrom;
                        anchor.isValid = true;
                        needsUpdate = true;
                        continue;
                    }
                }
            }

            // Fall back to text search
            const newPosition = this._findTextInClaims(
                state.doc,
                anchor.text,
                anchor.contextBefore,
                anchor.contextAfter,
                anchor.lastValidatedOffset
            );

            if (newPosition) {
                anchor.startOffset = newPosition.from;
                anchor.endOffset = newPosition.to;
                anchor.lastValidatedOffset = newPosition.from;
                anchor.isValid = true;
                needsUpdate = true;
            } else {
                anchor.isValid = false;
                needsUpdate = true;
            }
        }

        if (needsUpdate) {
            this._updateClaimsDecorations(view);
        }
    }

    /**
     * Navigate to an anchor position
     */
    navigateToAnchor(anchorId, editor) {
        const anchor = this.anchors.get(anchorId);
        if (!anchor || !anchor.isValid) {
            console.warn('Cannot navigate to invalid anchor');
            return false;
        }

        if (anchor.documentType === 'spec' && editor.view) {
            // For TipTap/ProseMirror
            const { state, dispatch } = editor.view;
            const tr = state.tr.setSelection(
                state.selection.constructor.create(state.doc, anchor.startOffset, anchor.endOffset)
            );
            dispatch(tr.scrollIntoView());
            editor.view.focus();
            return true;
        } else if (anchor.documentType === 'claims' && editor.view) {
            // For CodeMirror
            editor.dispatch({
                selection: { anchor: anchor.startOffset, head: anchor.endOffset },
                scrollIntoView: true
            });
            editor.focus();
            return true;
        }

        return false;
    }

    /**
     * Get visual decoration style
     */
    getDecorationClass(anchorId) {
        const anchor = this.anchors.get(anchorId);
        if (!anchor) return 'comment-anchor';
        
        return anchor.isValid ? 'comment-anchor' : 'comment-anchor-invalid';
    }

    // Private helper methods

    _getBlockPath(resolvedPos) {
        const path = [];
        for (let i = 0; i < resolvedPos.depth; i++) {
            path.push({
                type: resolvedPos.node(i).type.name,
                index: resolvedPos.index(i)
            });
        }
        return path;
    }

    _findTextInDocument(doc, text, contextBefore, contextAfter, lastOffset) {
        const searchText = contextBefore + text + contextAfter;
        const content = doc.textContent;
        
        // Try near the last known position first
        let searchStart = Math.max(0, lastOffset - 100);
        let index = content.indexOf(searchText, searchStart);
        
        if (index === -1) {
            // Search the whole document
            index = content.indexOf(searchText);
        }
        
        if (index !== -1) {
            const from = index + contextBefore.length;
            const to = from + text.length;
            return { from, to };
        }
        
        // Try searching for just the text without context
        index = content.indexOf(text, searchStart);
        if (index !== -1) {
            return { from: index, to: index + text.length };
        }
        
        return null;
    }

    _findTextInClaims(doc, text, contextBefore, contextAfter, lastOffset) {
        // Similar to _findTextInDocument but uses CodeMirror's text model
        const searchText = contextBefore + text + contextAfter;
        const content = doc.toString();
        
        let searchStart = Math.max(0, lastOffset - 100);
        let index = content.indexOf(searchText, searchStart);
        
        if (index === -1) {
            index = content.indexOf(searchText);
        }
        
        if (index !== -1) {
            const from = index + contextBefore.length;
            const to = from + text.length;
            return { from, to };
        }
        
        index = content.indexOf(text, searchStart);
        if (index !== -1) {
            return { from: index, to: index + text.length };
        }
        
        return null;
    }

    _addEditorAnchor(editor, anchorId) {
        if (!this.editorAnchors.has(editor)) {
            this.editorAnchors.set(editor, new Set());
        }
        this.editorAnchors.get(editor).add(anchorId);
    }

    _updateSpecDecorations(_editor) {
        // This will be implemented with ProseMirror decorations
        // For now, just log
        console.log('Updating spec decorations');
    }

    _updateClaimsDecorations(_view) {
        // This will be implemented with CodeMirror decorations
        // For now, just log
        console.log('Updating claims decorations');
    }

    /**
     * Remove an anchor
     */
    removeAnchor(anchorId) {
        this.anchors.delete(anchorId);

        // Remove from all editor anchor sets
        for (const [_editor, anchorSet] of this.editorAnchors) {
            anchorSet.delete(anchorId);
        }
    }

    /**
     * Get all anchors for a document type
     */
    getAnchorsForDocument(documentType) {
        const results = [];
        for (const anchor of this.anchors.values()) {
            if (anchor.documentType === documentType) {
                results.push(anchor);
            }
        }
        return results;
    }
}

// Export singleton instance
export const commentAnchorService = new CommentAnchorService();

// Expose to window for debugging
if (typeof window !== 'undefined') {
    window.commentAnchorService = commentAnchorService;
}